import { Injectable } from "@angular/core";

import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, Subject } from "rxjs";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { ConfigurationService } from "./configuration.service";
import { StorageService } from "./storage.service";
import { IIdentityUser } from "../models/identity-user.model";
import * as jwt_decode from "jwt-decode";

@Injectable()
export class SecurityService {
  private actionUrl: string;
  private headers: HttpHeaders;
  private storage: StorageService;
  private authenticationSource = new Subject<boolean>();
  authenticationChallenge$ = this.authenticationSource.asObservable();
  private authorityUrl = "";

  public IsAuthorized: boolean;

  public UserData: any;

  constructor(
    private _http: HttpClient,
    private _router: Router,
    private route: ActivatedRoute,
    private _configurationService: ConfigurationService,
    private _storageService: StorageService
  ) {
    this.headers = new HttpHeaders();
    this.headers.append("Content-Type", "application/json");
    this.headers.append("Accept", "application/json");
    this.storage = _storageService;

    if (this._configurationService.isReady) {
      this.authorityUrl = this._configurationService.serverSettings.identityUrl;
      this.storage.store("IdentityUrl", this.authorityUrl);
    } else {
      this._configurationService.settingsLoaded$.subscribe((x) => {
        this.authorityUrl =
          this._configurationService.serverSettings.identityUrl;
        this.storage.store("IdentityUrl", this.authorityUrl);
      });
    }

    if (this.storage.retrieve("IsAuthorized") !== "") {
      this.IsAuthorized = this.storage.retrieve("IsAuthorized");
      this.authenticationSource.next(true);
      this.UserData = this.storage.retrieve("userData");
    }
  }

  public GetToken(): any {
    return this.storage.retrieve("authorizationData");
  }

  public ResetAuthorizationData() {
    this.storage.store("authorizationData", "");
    this.storage.store("authorizationDataIdToken", "");

    this.IsAuthorized = false;
    this.storage.store("IsAuthorized", false);
    this.storage.store("userData", "");
  }

  public GetUserEmail() {
    return this.UserData.email;
  }

  public GetUserRole() {
    return this.UserData.role;
  }

  public GetUser(): IIdentityUser {
    return this.UserData;
  }

  public SetUser(user: IIdentityUser) {
    return this.storage.store("userData", user);
  }

  public SetAuthorizationData(token: any, id_token: any) {
    if (this.storage.retrieve("authorizationData") !== "") {
      this.storage.store("authorizationData", "");
    }

    this.storage.store("authorizationData", token);
    this.storage.store("authorizationDataIdToken", id_token);
    this.IsAuthorized = true;
    this.storage.store("IsAuthorized", true);

    this.getUserData().subscribe(
      (data) => {
        this.UserData = data;
        this.storage.store("userData", data);
        // emit observable
        this.authenticationSource.next(true);
      },
      (error) => this.HandleError(error),
      () => {
        console.log(this.UserData);
      }
    );
  }

  public Authorize() {
    this.ResetAuthorizationData();

    const authorizationUrl = this.authorityUrl + "/connect/authorize";
    const client_id = "spaclient";
    const redirect_uri = location.href;
    const response_type = "id_token token";
    const scope =
      "openid profile IdentityServerApi webagg bidding reporting apps mediation levelplay applovinmediation applovinoptimization applovinreporting " +
      "admobreportingcollector unityadsreportingcollector applovinreportingcollector ironsourcereportingcollector chartboostreportingcollector " +
      "fyberreportingcollector facebookreportingcollector a4greportingcollector reklamupreportingcollector admanagerreportingcollector exmarketreportingcollector " +
      "externalreporting makrooreportingcollector potensusreportingcollector premiumadsreportingcollector iionreportingcollector jungletechnologyreportingcollector " +
      "multimediationoptimization";
    const nonce = "N" + Math.random() + "" + Date.now();
    const state = Date.now() + "" + Math.random();

    this.storage.store("authStateControl", state);
    this.storage.store("authNonce", nonce);

    const url =
      authorizationUrl +
      "?" +
      "response_type=" +
      encodeURI(response_type) +
      "&" +
      "client_id=" +
      encodeURI(client_id) +
      "&" +
      "redirect_uri=" +
      encodeURI(redirect_uri) +
      "&" +
      "scope=" +
      encodeURI(scope) +
      "&" +
      "nonce=" +
      encodeURI(nonce) +
      "&" +
      "state=" +
      encodeURI(state);

    window.location.href = url;
  }

  public AuthorizedCallback(): boolean {
    this.ResetAuthorizationData();

    const hash = window.location.hash.substr(1);

    const result: any = hash
      .split("&")
      .reduce(function (_result: any, item: string) {
        const parts = item.split("=");
        _result[parts[0]] = parts[1];
        return _result;
      }, {});

    let token = "";
    let id_token = "";
    let authResponseIsValid = false;

    if (!result.error) {
      if (result.state !== this.storage.retrieve("authStateControl")) {
        console.log("AuthorizedCallback incorrect state");
      } else {
        token = result.access_token;
        id_token = result.id_token;

        const dataIdToken: any = this.getDataFromToken(id_token);

        // validate nonce
        if (dataIdToken.nonce !== this.storage.retrieve("authNonce")) {
          console.log("AuthorizedCallback incorrect nonce");
        } else {
          this.storage.store("authNonce", "");
          this.storage.store("authStateControl", "");

          authResponseIsValid = true;
          console.log(
            "AuthorizedCallback state and nonce validated, returning access token"
          );
        }
      }
    }

    if (authResponseIsValid) {
      this.SetAuthorizationData(token, id_token);
    }

    return authResponseIsValid;
  }

  public Logoff() {
    const authorizationUrl = this.authorityUrl + "/connect/endsession";
    const id_token_hint = this.storage.retrieve("authorizationDataIdToken");
    const post_logout_redirect_uri = location.origin + "/";

    const url =
      authorizationUrl +
      "?" +
      "id_token_hint=" +
      encodeURI(id_token_hint) +
      "&" +
      "post_logout_redirect_uri=" +
      encodeURI(post_logout_redirect_uri);

    this.ResetAuthorizationData();
    // emit observable
    this.authenticationSource.next(false);
    window.location.href = url;
  }

  public PostLogout() {
    this.ResetAuthorizationData();
    // emit observable
    this.authenticationSource.next(false);
  }

  public HandleError(error: any) {
    if (error.status === 403) {
      throw new Error("Forbidden");
    } else if (error.status === 401) {
      throw new Error("Unauthorized");
    }
  }

  public isTokenExpired(token?: string): boolean {
    if (!token) {
      token = this.GetToken();
    }
    if (!token) {
      return true;
    }

    const date = this.getTokenExpirationDate(token);
    if (date === undefined) {
      return false;
    }
    return !(date.valueOf() > new Date().valueOf());
  }

  private getTokenExpirationDate(token: string): Date {
    const decoded = jwt_decode(token);

    if (decoded.exp === undefined) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  private urlBase64Decode(str: string) {
    let output = str.replace("-", "+").replace("_", "/");
    switch (output.length % 4) {
      case 0:
        break;
      case 2:
        output += "==";
        break;
      case 3:
        output += "=";
        break;
      default:
        throw new Error("Illegal base64url string!");
    }

    return window.atob(output);
  }

  private getDataFromToken(token: any) {
    let data = {};

    if (typeof token !== "undefined") {
      const encoded = token.split(".")[1];

      data = JSON.parse(this.urlBase64Decode(encoded));
    }

    return data;
  }

  private getUserData = (): Observable<string[]> => {
    if (this.authorityUrl === "") {
      this.authorityUrl = this.storage.retrieve("IdentityUrl");
    }

    const options = this.setHeaders();

    return this._http
      .get<string[]>(`${this.authorityUrl}/connect/userinfo`, options)
      .pipe<string[]>((info: any) => info);
  };

  private setHeaders(): any {
    const httpOptions = {
      headers: new HttpHeaders(),
    };

    httpOptions.headers = httpOptions.headers.set(
      "Content-Type",
      "application/json"
    );
    httpOptions.headers = httpOptions.headers.set("Accept", "application/json");

    const token = this.GetToken();

    if (token !== "") {
      httpOptions.headers = httpOptions.headers.set(
        "Authorization",
        `Bearer ${token}`
      );
    }

    return httpOptions;
  }
}
